import { useCallback } from "react";
// src/components/superAdmin/SuperAdminBluePrints.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Shield, ChevronDown, ChevronRight, CheckCircle2, ShieldCheck, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../firebase/AuthProvider';
import { BlueprintPermissionRef, BlueprintRoleDto, BlueprintScopeRef } from '../../../../interfaces/superAdmin/SuperAdminBlueprints';
import { createBlueprint, deleteBlueprint, getAllBlueprints, getBlueprintReferenceData, updateBlueprint } from '../../../../services/superAdminServices/SuperAdminBlueprintService';

const formatCatalogLabel = (value: string) => value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[:_.-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(' ');

const SuperAdminBluePrints: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [, setLoading] = useState(false);
    const [blueprints, setBlueprints] = useState<BlueprintRoleDto[]>([]);
    const [refData, setRefData] = useState<BlueprintPermissionRef[]>([]);
    
    // State to determine if we are creating a new one (blueprintKey editable) or updating
    const [isNewBlueprint, setIsNewBlueprint] = useState(false);
    const [editingBlueprint, setEditingBlueprint] = useState<BlueprintRoleDto | null>(null);

    // UI Grouping
    const [expandedModules, setExpandedModules] = useState<string[]>([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        
        const [fetchedBlueprints, fetchedRef] = await Promise.all([
            getAllBlueprints(authToken),
            getBlueprintReferenceData(authToken)
        ]);

        if (fetchedBlueprints) setBlueprints(fetchedBlueprints);
        if (fetchedRef) setRefData(fetchedRef);
        setLoading(false);
    }, [currentUser]);

useEffect(() => { loadData(); }, [currentUser, loadData]);

    // Collapse all modules when switching blueprints
    useEffect(() => {
        setExpandedModules([]);
    }, [editingBlueprint?.blueprintKey, editingBlueprint?.id]);



    const handleCreateNew = () => {
        setIsNewBlueprint(true);
        setEditingBlueprint({
            blueprintKey: "",
            displayName: "New Blueprint",
            description: "",
            isActive: true,
            assignments: []
        });
    };

    const handleSelectBlueprint = (blueprint: BlueprintRoleDto) => {
        setIsNewBlueprint(false);
        // Deep copy to prevent state mutation and ensure assignments array exists
        const blueprintCopy = JSON.parse(JSON.stringify(blueprint));
        if (!blueprintCopy.assignments) blueprintCopy.assignments = [];
        setEditingBlueprint(blueprintCopy); 
    };

    const handleSave = async () => {
        if (!editingBlueprint || !editingBlueprint.displayName || !editingBlueprint.blueprintKey || !currentUser) {
            toast.error("Blueprint Key and Display Name are required.");
            return;
        }

        setLoading(true);
        const authToken = await currentUser.getIdToken();
        let result = null;

        if (isNewBlueprint) {
            result = await createBlueprint(authToken, {
                blueprintKey: editingBlueprint.blueprintKey,
                displayName: editingBlueprint.displayName,
                description: editingBlueprint.description,
                isActive: editingBlueprint.isActive,
                assignments: editingBlueprint.assignments || []
            });
        } else {
            result = await updateBlueprint(authToken, editingBlueprint.blueprintKey, {
                displayName: editingBlueprint.displayName,
                description: editingBlueprint.description,
                isActive: editingBlueprint.isActive,
                assignments: editingBlueprint.assignments || []
            });
        }
        
        if (result) {
            await loadData();
            setIsNewBlueprint(false);
            
            // Ensure assignments array exists on the returned object to prevent UI crashes
            if (!result.assignments) result.assignments = [];
            setEditingBlueprint(result);
            
            toast.success("Blueprint saved successfully.");
        } else {
            toast.error("Failed to save blueprint.");
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!editingBlueprint || isNewBlueprint || editingBlueprint.isSystem || !currentUser) return;
        if (!window.confirm(`Are you sure you want to permanently delete the blueprint: ${editingBlueprint.blueprintKey}?`)) return;

        setLoading(true);
        const authToken = await currentUser.getIdToken();
        const success = await deleteBlueprint(authToken, editingBlueprint.blueprintKey);
        
        if (success) {
            toast.success("Blueprint deleted successfully.");
            setEditingBlueprint(null);
            await loadData();
        } else {
            toast.error("Failed to delete blueprint.");
        }
        setLoading(false);
    }

    // --- PERMISSION TOGGLE LOGIC ---

    const toggleExpandModule = (module: string) => {
        setExpandedModules(prev => prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]);
    };

    const toggleScope = (permissionKey: string, scope: string, module: string) => {
        if (!editingBlueprint) return;

        const currentAssignments = editingBlueprint.assignments || [];
        const exists = currentAssignments.some(
            a => a.permissionKey === permissionKey && a.scope === scope
        );

        let newAssignments = [...currentAssignments];

        if (exists) {
            newAssignments = newAssignments.filter(
                a => !(a.permissionKey === permissionKey && a.scope === scope)
            );
        } else {
            const permission = refData.find(item => item.permissionKey === permissionKey);
            const availableScopes: BlueprintScopeRef[] = permission?.scopes || (permission?.availableScopes || []).map(value => ({ scope: value }));
            const selectedScope = availableScopes.find(item => item.scope === scope);

            if (selectedScope) {
                newAssignments = newAssignments.filter(assignment => {
                    if (assignment.permissionKey !== permissionKey) return true;
                    const existingScope = availableScopes.find(item => item.scope === assignment.scope);
                    if (!existingScope) return true;

                    const sameGroup = Boolean(
                        selectedScope.exclusiveGroup &&
                        existingScope.exclusiveGroup === selectedScope.exclusiveGroup
                    );

                    if (selectedScope.selectionMode === 'single' && sameGroup) return false;
                    if (selectedScope.isUniversal && sameGroup) return false;
                    if (existingScope.isUniversal && sameGroup) return false;
                    return true;
                });
            }
            newAssignments.push({ permissionKey, scope, module });
        }

        setEditingBlueprint({ ...editingBlueprint, assignments: newAssignments });
    };

    // Group Reference Data by Module
    const groupedRefData = refData.reduce((acc, curr) => {
        const moduleName = curr.module || "General";
        if (!acc[moduleName]) acc[moduleName] = [];
        acc[moduleName].push(curr);
        return acc;
    }, {} as Record<string, BlueprintPermissionRef[]>);

    const sortedModules = Object.keys(groupedRefData).sort((left, right) =>
        formatCatalogLabel(left).localeCompare(formatCatalogLabel(right))
    );
    const sortedBlueprints = [...blueprints].sort((left, right) => {
        if (left.isSystem !== right.isSystem) return left.isSystem ? -1 : 1;
        return left.displayName.localeCompare(right.displayName);
    });
    const isOwnerDefault = editingBlueprint?.isSystem && editingBlueprint.systemKey === 'ORG_OWNER';
    const ownerUnconfiguredCount = isOwnerDefault
        ? refData.filter(permission => !(editingBlueprint?.assignments || []).some(assignment => assignment.permissionKey === permission.permissionKey)).length
        : 0;

    return (
        <div className="w-full h-screen flex flex-col p-4 bg-gray-50 overflow-hidden">
            {/* Header Area */}
            <div className="mb-4 flex items-center justify-between flex-shrink-0">
                <button onClick={() => navigate(-1)} className="flex items-center text-[#7B5CD6] hover:text-[#5e45a3] font-medium text-sm transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </button>
                <h1 className="text-xl font-bold text-gray-800 mb-0">Blueprint Manager</h1>
            </div>

            {/* Main Content Grid - min-h-0 is required to allow internal scrolling */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 min-h-0">
                
                {/* LEFT SIDEBAR: BLUEPRINT LIST */}
                <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white flex-shrink-0">
                        <h3 className="font-semibold text-gray-800 text-sm tracking-wide mb-0">Templates</h3>
                        <button 
                            onClick={handleCreateNew} 
                            className="flex items-center text-xs bg-[#7B5CD6] text-white px-3 py-1.5 rounded hover:bg-[#6a4bc0] transition-colors shadow-sm"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> New
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-white">
                        {sortedBlueprints.map(blueprint => (
                            <div 
                                key={blueprint.id || blueprint.blueprintKey} 
                                onClick={() => handleSelectBlueprint(blueprint)} 
                                className={`p-3 rounded-md cursor-pointer transition-all border-l-4 ${editingBlueprint?.blueprintKey === blueprint.blueprintKey && !isNewBlueprint ? 'bg-purple-50 border-[#7B5CD6]' : 'border-transparent hover:bg-gray-50'}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="font-semibold text-gray-900 text-sm">{blueprint.displayName}</span>
                                    {blueprint.isSystem && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                                            <ShieldCheck className="h-3 w-3" /> System default
                                        </span>
                                    )}
                                    {!blueprint.isActive && (
                                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Draft</span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-400 font-mono truncate">{blueprint.blueprintKey}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT PANEL: EDITOR */}
                <div className="col-span-1 md:col-span-9 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
                    {editingBlueprint ? (
                        <>
                            {/* Toolbar */}
                            <div className="p-6 border-b border-gray-200 bg-white flex-shrink-0">
                                {isOwnerDefault && (
                                    <div className="mb-5 rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900">
                                        <div className="flex items-start gap-2">
                                            <Info className="mt-0.5 h-4 w-4 shrink-0" />
                                            <div>
                                                <p className="m-0 font-semibold">Default for new Organization Owner roles</p>
                                                <p className="mb-0 mt-1 text-xs leading-relaxed text-purple-700">
                                                    Saving publishes version {(editingBlueprint.version || 0) + 1} for future organizations only. Existing organizations are not changed.
                                                </p>
                                                {ownerUnconfiguredCount > 0 && (
                                                    <p className="mb-0 mt-2 font-semibold text-amber-700">
                                                        {ownerUnconfiguredCount} active permission{ownerUnconfiguredCount === 1 ? '' : 's'} currently have no owner-default assignment.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
                                    <div className="flex-1 w-full space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Display Name</label>
                                            <input 
                                                className="w-full text-sm bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-gray-900 placeholder-gray-400 transition-all shadow-sm" 
                                                value={editingBlueprint.displayName}
                                                onChange={(e) => setEditingBlueprint({ ...editingBlueprint, displayName: e.target.value })}
                                                placeholder="e.g. Support Coordinator"
                                            />
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <div className="flex-1">
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">System Key</label>
                                                <input 
                                                    className={`w-full text-sm bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] font-mono text-gray-700 placeholder-gray-400 transition-all shadow-sm ${!isNewBlueprint ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' : ''}`} 
                                                    value={editingBlueprint.blueprintKey}
                                                    onChange={(e) => setEditingBlueprint({ ...editingBlueprint, blueprintKey: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                                                    placeholder="e.g. SUPPORT_COORDINATOR"
                                                    disabled={!isNewBlueprint}
                                                />
                                            </div>
                                            <div className="flex items-end pb-2">
                                                <label className="flex items-center space-x-2 cursor-pointer group">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 rounded border-gray-300 text-[#7B5CD6] focus:ring-[#7B5CD6] transition-colors cursor-pointer"
                                                        checked={editingBlueprint.isActive}
                                                        onChange={(e) => setEditingBlueprint({ ...editingBlueprint, isActive: e.target.checked })}
                                                        disabled={Boolean(editingBlueprint.isSystem)}
                                                    />
                                                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">Visible during onboarding</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Description</label>
                                            <input 
                                                className="w-full text-sm bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-gray-700 placeholder-gray-400 transition-all shadow-sm" 
                                                value={editingBlueprint.description || ""}
                                                onChange={(e) => setEditingBlueprint({ ...editingBlueprint, description: e.target.value })}
                                                placeholder="Explain this blueprint's intended use case..."
                                            />
                                        </div>
                                    </div>
                                    <div className="flex lg:flex-col gap-3 w-full lg:w-48 shrink-0">
                                        <button 
                                            onClick={handleSave} 
                                            className="flex-1 lg:flex-none flex items-center justify-center px-4 py-2 bg-[#7B5CD6] text-white rounded text-sm font-medium hover:bg-[#6a4bc0] transition-colors shadow-sm"
                                        >
                                            <Save className="w-4 h-4 mr-2"/> Save Changes
                                        </button>
                                        {!isNewBlueprint && !editingBlueprint.isSystem && (
                                            <button 
                                                onClick={handleDelete} 
                                                className="flex-1 lg:flex-none flex items-center justify-center px-4 py-2 bg-white text-red-500 border border-red-200 rounded text-sm font-medium hover:bg-red-50 transition-colors shadow-sm"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2"/> Delete Template
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* CONTENT AREA: PERMISSION MODULES */}
                            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 space-y-4">
                                <div className="mb-4 px-1">
                                    <h3 className="text-sm font-bold text-gray-800 mb-0">Permission Configuration</h3>
                                </div>
                                
                                {sortedModules.map(moduleName => {
                                    const modulePermissions = groupedRefData[moduleName];
                                    const isExpanded = expandedModules.includes(moduleName);
                                    
                                    // SAFELY access assignments array here
                                    const currentAssignments = editingBlueprint.assignments || [];
                                    const activeModuleCount = currentAssignments.filter(
                                        a => modulePermissions.some(mp => mp.permissionKey === a.permissionKey)
                                    ).length;

                                    return (
                                        <div key={moduleName} className={`bg-white border rounded-lg shadow-sm transition-all ${isExpanded ? 'border-[#7B5CD6]/40' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <div 
                                                onClick={() => toggleExpandModule(moduleName)}
                                                className={`px-4 py-3 flex justify-between items-center cursor-pointer select-none transition-colors rounded-t-lg ${isExpanded ? 'bg-purple-50/50 border-b border-gray-100' : 'bg-white rounded-b-lg'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-1 rounded-md transition-colors ${isExpanded ? 'bg-purple-100 text-[#7B5CD6]' : 'bg-gray-100 text-gray-400'}`}>
                                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-800 text-sm mb-0">{formatCatalogLabel(moduleName)}</h4>
                                                    </div>
                                                </div>
                                                
                                                {activeModuleCount > 0 && (
                                                    <span className="flex items-center gap-1.5 bg-purple-50 text-[#7B5CD6] text-xs px-2 py-0.5 rounded border border-purple-200 font-bold">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        {activeModuleCount} Active Rules
                                                    </span>
                                                )}
                                            </div>

                                            {/* CSS Grid technique for smooth height animation */}
                                            <div 
                                                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                                                    isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                                }`}
                                            >
                                                <div className="overflow-hidden">
                                                    <div className="p-4 bg-white space-y-6 rounded-b-lg">
                                                        {modulePermissions.map(permDef => (
                                                            <div key={permDef.permissionKey} className="pb-4 last:pb-0 border-b last:border-0 border-gray-100">
                                                                <div className="mb-3">
                                                                    <h5 className="font-semibold text-gray-800 text-sm bg-gray-50 inline-block px-2 py-1 rounded border border-gray-200 mb-0">
                                                                        {permDef.displayName || formatCatalogLabel(permDef.permissionKey)}
                                                                    </h5>
                                                                    <div className="mt-1 text-[11px] font-mono text-gray-400">{permDef.permissionKey}</div>
                                                                    {permDef.description && <p className="mb-0 mt-2 text-xs leading-relaxed text-gray-500">{permDef.description}</p>}
                                                                </div>
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                                                    {((permDef.scopes || permDef.availableScopes.map(scope => ({ scope }))) as BlueprintScopeRef[]).map(scopeRef => {
                                                                        const scope = scopeRef.scope;
                                                                        const isChecked = currentAssignments.some(
                                                                            a => a.permissionKey === permDef.permissionKey && a.scope === scope
                                                                        );

                                                                        return (
                                                                            <label 
                                                                                key={scope} 
                                                                                className={`flex items-start p-3 rounded border cursor-pointer transition-all ${isChecked ? 'bg-purple-50 border-[#7B5CD6] shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                                                                            >
                                                                                <div className="flex items-center h-5">
                                                                                    <input 
                                                                                        type="checkbox"
                                                                                        className="w-4 h-4 rounded border-gray-300 text-[#7B5CD6] focus:ring-[#7B5CD6] transition-colors"
                                                                                        checked={isChecked}
                                                                                        onChange={() => toggleScope(permDef.permissionKey, scope, moduleName)}
                                                                                    />
                                                                                </div>
                                                                                <span className={`ml-3 min-w-0 flex-1 break-words text-sm ${isChecked ? 'font-bold text-[#7B5CD6]' : 'text-gray-600'}`}>
                                                                                    <span className="block">{scopeRef.displayName || formatCatalogLabel(scope)}</span>
                                                                                    {scopeRef.description && (
                                                                                        <span className="mt-1 block text-xs font-normal leading-snug text-gray-500">{scopeRef.description}</span>
                                                                                    )}
                                                                                </span>
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50">
                            <Shield className="w-16 h-16 mb-4 text-gray-300" strokeWidth={1.5} />
                            <p className="text-gray-500 font-medium">Select a blueprint template from the left to configure permissions.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuperAdminBluePrints;
